import { FlexBox } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ProposalTemplateTag } from '../../data-access/proposalTemplateTags';

export const groupingNameOptions: Record<
  ProposalTemplateTag['groupingName'],
  string
> = {
  CAPACITY: 'Capacity',
  SYSTEM_TYPE: 'System Type',
  BRANDS: 'Brands',
  ADDONS: 'Add ons',
};

type TagGroup = {
  groupName: ProposalTemplateTag['groupingName'];
  tags: ProposalTemplateTag[];
};

const ProposalTemplateOptionsFilterFormInputSchema = z.object({
  searchTerm: z.string(),
  tags: z.string().array(),
});
export type ProposalTemplateOptionsFilterFormInputs = z.infer<
  typeof ProposalTemplateOptionsFilterFormInputSchema
>;

interface ProposalTemplateOptionsFilterFormProps {
  searchPlaceholder?: string;
  proposalTemplateTags: ProposalTemplateTag[];
  onChange: (data: ProposalTemplateOptionsFilterFormInputs) => void;
}

const ProposalTemplateOptionsFilterForm: React.FC<
  ProposalTemplateOptionsFilterFormProps
> = ({ searchPlaceholder, proposalTemplateTags, onChange }) => {
  const { control, formState, reset, watch } =
    useForm<ProposalTemplateOptionsFilterFormInputs>({
      defaultValues: {
        searchTerm: '',
        tags: [],
      },
      resolver: zodResolver(ProposalTemplateOptionsFilterFormInputSchema),
    });

  useEffect(() => {
    const subscription = watch((value) => {
      const searchTerm = value.searchTerm || '';
      const tags = (value.tags || []).flatMap((t) => t || []);
      if (searchTerm || tags) {
        onChange({ searchTerm, tags });
      }
    });
    return () => subscription.unsubscribe();
  }, [onChange, watch]);

  const groupedProposalTemplateTags = useMemo(
    () =>
      proposalTemplateTags.reduce<TagGroup[]>((acc, tag) => {
        const group = acc.find((g) => g.groupName === tag.groupingName);
        if (group) {
          group.tags.push(tag);
        } else {
          acc.push({ groupName: tag.groupingName, tags: [tag] });
        }
        return acc;
      }, []),
    [proposalTemplateTags]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      <FlexBox gap={2}>
        <Controller
          name="searchTerm"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={
                searchPlaceholder || 'Search by option, product or rebate name'
              }
              type="search"
              fullWidth
            />
          )}
        />
        <Button
          type="button"
          color={formState.isDirty ? 'softBlue' : 'softGold'}
          onClick={() => reset()}
        >
          Clear
        </Button>
      </FlexBox>

      <Controller
        name="tags"
        control={control}
        render={({ field }) => (
          <FlexBox
            flexDirection={['column', 'row']}
            alignItems={['flex-start', 'center']}
            justifyContent={['flex-start', 'center']}
            gap={2}
            width={'100%'}
          >
            {groupedProposalTemplateTags
              .sort((a, b) => a.groupName.localeCompare(b.groupName))
              .map(({ groupName, tags }) => (
                <TextField
                  {...field}
                  key={groupName}
                  label={groupingNameOptions[groupName]}
                  fullWidth
                  select
                  slotProps={{ select: { multiple: true, ...field } }}
                >
                  <MenuItem value={''} sx={{ display: 'none' }}>
                    Select a Value
                  </MenuItem>
                  {tags.map(({ proposalTemplateTagId, name }) => (
                    <MenuItem
                      key={proposalTemplateTagId}
                      value={proposalTemplateTagId}
                      selected={field.value.includes(proposalTemplateTagId)}
                    >
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              ))}
          </FlexBox>
        )}
      />
    </Box>
  );
};

export default ProposalTemplateOptionsFilterForm;
